import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import {
  cancelVoucher,
  confirmVoucher,
  reserveVoucher,
} from "../../store/voucher/voucher.actions";

/**
 * Hält den Idempotenz-Schlüssel eines Einlösevorgangs.
 *
 * ⚠ Der Schlüssel gilt für den ganzen Vorgang — reservieren, bestätigen und
 * abbrechen beziehen sich auf dieselbe Reservierung. Jolioo dedupliziert nicht,
 * ein Wiederholungsversuch muss also denselben Schlüssel verwenden wie der
 * erste; nur ein *neuer* Vorgang bekommt einen neuen.
 */
@Injectable({
  providedIn: "root",
})
export class VoucherRedemptionService {
  private _key: string | null = null;

  constructor(private _store: Store) {}

  /** Startet einen neuen Vorgang. Nach Abschluss oder Abbruch aufrufen. */
  public begin(): void {
    this._key = VoucherRedemptionService.newKey();
  }

  /**
   * crypto.randomUUID statt der uuid-Library: die ist ESM-only und bricht unter
   * jest. Der Fallback deckt ältere WebViews ohne secure context ab.
   */
  private static newKey(): string {
    const webCrypto = globalThis.crypto;

    if (webCrypto && typeof webCrypto.randomUUID === "function") {
      return webCrypto.randomUUID();
    }

    return `${Date.now().toString(16)}-${Math.random()
      .toString(16)
      .slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`;
  }

  /** Schritt 1 — reserviert den Betrag, bucht noch nichts ab. */
  public reserve(code: string, pharmacy: string, value: number): void {
    if (!this._key) {
      this.begin();
    }

    this._store.dispatch(
      reserveVoucher({
        code,
        pharmacy,
        value,
        idempotencyKey: this._key as string,
      }),
    );
  }

  /** Erneut reservieren — bewusst mit demselben Schlüssel. */
  public retryReserve(code: string, pharmacy: string, value: number): void {
    this.reserve(code, pharmacy, value);
  }

  /** Schritt 2a — die Apotheker:in bestätigt. */
  public confirm(code: string, pharmacy: string): void {
    if (!this._key) {
      return;
    }

    this._store.dispatch(
      confirmVoucher({ code, pharmacy, idempotencyKey: this._key }),
    );
  }

  /** Schritt 2b — die Apotheker:in bricht ab, der Betrag geht zurück. */
  public cancel(code: string, pharmacy: string): void {
    if (!this._key) {
      return;
    }

    this._store.dispatch(
      cancelVoucher({ code, pharmacy, idempotencyKey: this._key }),
    );
  }

  public complete(): void {
    this._key = null;
  }

  get key(): string | null {
    return this._key;
  }
}
